import { useCallback, useState, useSyncExternalStore } from 'react';
import { detectPlatform, isStandalone, type AddToHomeScreenPlatform } from './add-to-home-screen.js';

/**
 * Installing the counter as an app — features/STAFF-COUNTER.md.
 *
 * The counter has been installable since v1 (manifest, worker, iOS tags), but
 * nothing told anybody to install it, so a barista opened a browser, found a
 * bookmark and typed a URL every shift. This module is the part that asks.
 *
 * ## Why the install comes before pairing
 *
 * On iOS a home-screen app keeps its own cookie jar, separate from Safari's.
 * A tablet paired in a Safari tab and *then* added to the home screen opens an
 * app that has never been paired, and the owner's ten-minute code is long gone.
 * So on iOS the pairing screen refuses to take a code until it is running from
 * the icon. Android's installed app shares the browser's cookies, so there the
 * order does not matter and the install is offered rather than required.
 *
 * Installing also protects the offline queue: Safari deletes a site's
 * IndexedDB after seven days without a visit, and home-screen apps are exempt.
 * A counter in a Safari tab over a closed week would lose unsynced stamps.
 *
 * ## Why the prompt is captured at boot
 *
 * Chromium fires `beforeinstallprompt` once, early, and only once per page
 * load. A listener added when the pairing screen mounts is usually too late —
 * the event has come and gone while the bundle was still parsing. So the
 * listener is attached from `registerCounterServiceWorker()`, synchronously in
 * `main.tsx`, and the deferred event is held here until a screen asks for it.
 */

/** Chromium's event. Not in the DOM lib because no other engine ships it. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installedThisSession = false;
let captureAttached = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Holds Chromium's install prompt for later. Idempotent, and a no-op where the
 * event does not exist — Safari and Firefox simply never fire it.
 */
export function captureInstallPrompt(): void {
  if (captureAttached) return;
  captureAttached = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this Chrome shows its own mini-infobar, at a moment of its
    // choosing, over whatever the barista is doing.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installedThisSession = true;
    notify();
  });
}

/**
 * Asks the browser not to evict the counter's storage under pressure.
 *
 * The offline queue lives in IndexedDB, which is "best effort" by default: a
 * cheap Android phone low on space may clear it, unsynced stamps included.
 * Chromium grants persistence silently to installed apps and to sites with
 * engagement, so this usually succeeds without a prompt; where it is refused or
 * unsupported there is nothing to do but carry on.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export interface CounterInstall {
  platform: AddToHomeScreenPlatform;
  /** Running from the home-screen icon, or just installed in this session. */
  installed: boolean;
  /** The browser handed over an install prompt — Chromium only. */
  canPrompt: boolean;
  /**
   * iOS outside the installed app. Pairing here would be lost on install, so
   * the pairing screen asks for the install first.
   */
  mustInstallBeforePairing: boolean;
  /** Opens the browser's install dialog. Resolves to whether it was accepted. */
  install: () => Promise<boolean>;
}

export function useCounterInstall(): CounterInstall {
  const canPrompt = useSyncExternalStore(
    subscribe,
    () => deferredPrompt !== null,
    () => false,
  );
  const justInstalled = useSyncExternalStore(
    subscribe,
    () => installedThisSession,
    () => false,
  );

  // Read on the first render, not in an effect: starting from `'other'` drew
  // the code field on an iPhone for one frame before withdrawing it, which is
  // exactly the field this hook exists to withhold. Neither value changes while
  // the page is open — installing opens a new one.
  const [platform] = useState<AddToHomeScreenPlatform>(() => detectPlatform());
  const [standalone] = useState(() => isStandalone());

  const install = useCallback(async () => {
    const event = deferredPrompt;
    if (!event) return false;
    // A prompt can be shown once. Dropped before awaiting so a double tap
    // cannot call it twice.
    deferredPrompt = null;
    notify();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
  }, []);

  const installed = standalone || justInstalled;

  return {
    platform,
    installed,
    canPrompt,
    mustInstallBeforePairing: platform === 'ios' && !standalone,
    install,
  };
}
